import React, { useState, useEffect } from 'react';
import { getShows, getUpdateStatus, runBatchUpdate, type UpdateStatus, type BatchUpdateResult } from './lib/api';
import { ShowCard } from './components/ShowCard';
import { ShowListItem } from './components/ShowListItem';
import { Pagination } from './components/Pagination';
import { PasswordProtection } from './components/PasswordProtection';
import type { Show } from './types/show';
import './App.css';

// 检查环境变量是否存在
if (!import.meta.env.VITE_ACCESS_PASSWORD) {
  throw new Error('Access password not configured. Please set VITE_ACCESS_PASSWORD in .env file.');
}

// 从环境变量获取密码
const ACCESS_PASSWORD = import.meta.env.VITE_ACCESS_PASSWORD;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
const DEFAULT_PAGE_SIZE = 20;

function App() {
  console.log('🎬 App component rendering');
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'short' | 'regular' | 'movie'>('all');
  const [ratingFilter, setRatingFilter] = useState<'rated' | 'unrated'>('rated');
  const [sortBy, setSortBy] = useState<'time' | 'rating' | 'rating_time'>('rating_time');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const savedPageSize = localStorage.getItem('pageSize');
    return savedPageSize ? Number(savedPageSize) : DEFAULT_PAGE_SIZE;
  });
  const [searchTerm, setSearchTerm] = useState('');

  // 批量更新相关状态
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResults, setUpdateResults] = useState<BatchUpdateResult[]>([]);
  const [showUpdatePanel, setShowUpdatePanel] = useState(false);

  useEffect(() => {
    localStorage.setItem('pageSize', pageSize.toString());
  }, [pageSize]);

  useEffect(() => {
    // 检查是否已经通过验证
    const auth = localStorage.getItem('auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // 加载数据的函数
  const loadShows = async () => {
    console.log('🔄 Starting to load shows...');
    try {
      const data = await getShows();
      console.log('📦 Received shows data:', {
        count: data.length,
        categories: [...new Set(data.map(show => show.category))],
        ratings: data.map(show => show.rating).filter(Boolean)
      });
      setShows(data);
    } catch (err) {
      console.error('❌ Error loading shows:', err);
      setError(err instanceof Error ? err.message : '加载数据失败');
    } finally {
      console.log('🏁 Finished loading shows');
      setLoading(false);
    }
  };

  useEffect(() => {
    // 只有在通过验证后才加载数据
    if (!isAuthenticated) return;

    console.log('📊 Loading shows effect triggered');
    loadShows();
  }, [isAuthenticated]);

  // 重置分页
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sortBy, pageSize, searchTerm]);

  // 加载更新状态
  const loadUpdateStatus = async () => {
    try {
      const status = await getUpdateStatus();
      setUpdateStatus(status);
    } catch (err) {
      console.error('加载更新状态失败:', err);
    }
  };

  // 运行批量更新
  const handleBatchUpdate = async (batchSize: number = 10) => {
    setIsUpdating(true);
    setUpdateResults([]);

    try {
      let hasMore = true;
      const results: BatchUpdateResult[] = [];

      while (hasMore && results.length < 20) { // 最多执行20次批次，避免无限循环
        console.log(`执行第 ${results.length + 1} 批次更新...`);

        const result = await runBatchUpdate(batchSize);
        results.push(result);
        setUpdateResults([...results]);

        hasMore = result.next_batch_recommended && result.processed > 0;

        if (hasMore) {
          // 批次间隔2秒
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // 更新完成后重新加载数据
      await Promise.all([loadUpdateStatus(), loadShows()]);

    } catch (err) {
      console.error('批量更新失败:', err);
      setError(err instanceof Error ? err.message : '批量更新失败');
    } finally {
      setIsUpdating(false);
    }
  };

  // 自动加载更新状态
  useEffect(() => {
    if (isAuthenticated) {
      loadUpdateStatus();
    }
  }, [isAuthenticated]);

  const filteredShows = shows
    .filter(show => {
      // 首先按搜索词筛选
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        const matchesTitle = show.title.toLowerCase().includes(searchLower);
        if (!matchesTitle) return false;
      }

      // 然后按评分状态筛选
      const hasRating = show.rating != null && show.rating > 0;
      if (ratingFilter === 'rated' && !hasRating) return false;
      if (ratingFilter === 'unrated' && hasRating) return false;

      // 最后按类型筛选
      switch (filter) {
        case 'all':
          return true;
        case 'movie':
          return show.type === 'movie';
        case 'regular':
          return show.type === 'tv' && show.category === 'regular';
        case 'short':
          return show.type === 'tv' && show.category === 'short';
        default:
          return true;
      }
    })
    .sort((a, b) => {
      if (sortBy === 'rating') {
        const ratingDiff = (b.rating || 0) - (a.rating || 0);
        if (ratingDiff !== 0) return ratingDiff;
        // 二级排序：按创建时间
        return new Date(b.time).getTime() - new Date(a.time).getTime();
      } else if (sortBy === 'rating_time') {
        // 按出分时间排序，没有出分时间的排在最后
        const aTime = a.rating_time ? new Date(a.rating_time).getTime() : 0;
        const bTime = b.rating_time ? new Date(b.rating_time).getTime() : 0;
        // 处理无效时间戳（NaN）
        const aTimeValid = !isNaN(aTime) ? aTime : 0;
        const bTimeValid = !isNaN(bTime) ? bTime : 0;
        const timeDiff = bTimeValid - aTimeValid;
        if (timeDiff !== 0) return timeDiff;
        // 二级排序：按ID确保稳定性
        return a.id.localeCompare(b.id);
      } else {
        // 按创建时间排序
        const aTime = new Date(a.time).getTime();
        const bTime = new Date(b.time).getTime();
        // 处理无效时间戳（NaN）
        const aTimeValid = !isNaN(aTime) ? aTime : 0;
        const bTimeValid = !isNaN(bTime) ? bTime : 0;
        const timeDiff = bTimeValid - aTimeValid;
        if (timeDiff !== 0) return timeDiff;
        // 二级排序：按ID确保稳定性
        return a.id.localeCompare(b.id);
      }
    });

  // 计算分页数据
  const totalItems = filteredShows.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentShows = filteredShows.slice(startIndex, endIndex);

  console.log('📋 Filtered shows:', {
    total: shows.length,
    filtered: filteredShows.length,
    currentPage,
    pageSize,
    showing: currentShows.length,
    filter,
    sortBy
  });

  // 如果未通过验证，显示密码输入界面
  if (!isAuthenticated) {
    return (
      <PasswordProtection
        onSuccess={() => setIsAuthenticated(true)}
        correctPassword={ACCESS_PASSWORD}
      />
    );
  }

  if (loading) {
    console.log('⏳ Rendering loading state');
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('❌ Rendering error state:', error);
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">豆瓣剧集评分追踪</h1>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => {
                  localStorage.removeItem('auth');
                  setIsAuthenticated(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                退出登录
              </button>
            </div>
          </div>


          <div className="flex flex-wrap gap-4">
            {/* 搜索功能 */}
            <div className="flex items-center space-x-2">
              <label className="text-gray-700">搜索：</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="输入电影/剧集名称"
                className="w-40 rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  清除
                </button>
              )}
            </div>

            {/* 批量更新面板 */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowUpdatePanel(!showUpdatePanel)}
                className={`px-4 py-2 text-sm rounded ${updateStatus?.update_recommended
                  ? 'bg-orange-100 text-orange-800 border border-orange-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
                  } hover:bg-opacity-80`}
              >
                批量更新 {updateStatus?.pending_update ? `(${updateStatus.pending_update}条待更新)` : ''}
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-gray-700">评分状态：</label>
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value as typeof ratingFilter)}
                className="rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="rated">有评分</option>
                <option value="unrated">无评分</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-gray-700">分类筛选：</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
                className="rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="all">全部</option>
                <option value="regular">剧集</option>
                <option value="movie">电影</option>
                <option value="short">短剧</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-gray-700">排序方式：</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="rating_time">出分时间</option>
                <option value="rating">评分</option>
                <option value="time">创建时间</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-gray-700">每页显示：</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>
                    {size} 条
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-gray-700">显示方式：</label>
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md border ${viewMode === 'grid'
                    ? 'bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:text-gray-900'
                    }`}
                >
                  网格
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b -ml-px ${viewMode === 'list'
                    ? 'bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:text-gray-900'
                    }`}
                >
                  列表
                </button>
              </div>
            </div>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentShows.map(show => (
              <ShowCard key={show.id} show={show} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="divide-y divide-gray-200">
              {currentShows.map(show => (
                <ShowListItem key={show.id} show={show} />
              ))}
            </div>
          </div>
        )}

        {currentShows.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">暂无数据</p>
          </div>
        )}

        {currentShows.length > 0 && (
          <div className="mt-4">
            <div className="text-sm text-gray-700 mb-4">
              显示 {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalItems)} 条，共 {totalItems} 条
            </div>
            <Pagination
              pageSize={pageSize}
              currentPage={currentPage}
              totalItems={totalItems}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}

        {/* 批量更新面板 */}
        {showUpdatePanel && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-3">批量更新控制面板</h3>

            {updateStatus && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{updateStatus.total_shows}</div>
                  <div className="text-sm text-gray-600">总记录数</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{updateStatus.pending_update}</div>
                  <div className="text-sm text-gray-600">待更新</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{updateStatus.recent_updated}</div>
                  <div className="text-sm text-gray-600">最近已更新</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{updateStatus.never_updated}</div>
                  <div className="text-sm text-gray-600">从未更新</div>
                </div>
              </div>
            )}

            <div className="flex gap-4 items-center mb-4">
              <button
                onClick={() => handleBatchUpdate(5)}
                disabled={isUpdating}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isUpdating ? '更新中...' : '开始更新 (小批次: 5条)'}
              </button>
              <button
                onClick={() => handleBatchUpdate(10)}
                disabled={isUpdating}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isUpdating ? '更新中...' : '开始更新 (中批次: 10条)'}
              </button>
              <button
                onClick={() => handleBatchUpdate(20)}
                disabled={isUpdating}
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isUpdating ? '更新中...' : '开始更新 (大批次: 20条)'}
              </button>
              <button
                onClick={loadUpdateStatus}
                disabled={isUpdating}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                刷新状态
              </button>
            </div>

            {/* 更新结果显示 */}
            {updateResults.length > 0 && (
              <div className="max-h-64 overflow-y-auto bg-white p-3 rounded border">
                <h4 className="font-medium text-gray-900 mb-2">更新结果:</h4>
                {updateResults.map((result, index) => (
                  <div key={index} className="mb-2 p-2 bg-gray-50 rounded text-sm">
                    <div className="flex justify-between items-center">
                      <span>批次 {index + 1}</span>
                      <span className="text-xs text-gray-500">{result.execution_time_ms}ms</span>
                    </div>
                    <div className="text-gray-700">
                      处理: {result.processed}, 有评分: {result.rated || 0}, 错误: {result.errors}, 剩余: {result.remaining}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{result.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
